import { useTranslation } from "react-i18next";

import { AVAILABLE_LANGUAGES } from "../constants/languages";
import {
  normalizeSupportedLanguageCode,
  resolveSupportedLanguageCode,
} from "../utils/resolveSupportedLanguageCode";
import { shouldReloadPageOnGoogleMapsRoute } from "../utils/shouldReloadPageOnGoogleMapsRoute";

const LANGUAGE_CACHE_KEY = "i18nextLng";

const rememberLanguage = (language: string) => {
  const supported = normalizeSupportedLanguageCode(language);
  if (!supported) {
    return;
  }

  try {
    localStorage.setItem(LANGUAGE_CACHE_KEY, supported);
  } catch {
    return;
  }
};

const publishLanguageChange = (
  emit: (event: "languageChanged", language: string) => void,
  language: string,
) => {
  queueMicrotask(() => {
    emit("languageChanged", language);
  });
};

export const useLocalization = () => {
  const { i18n, t: translate } = useTranslation();

  const changeLanguage = async (
    languageCode: string,
    options?: { reload?: boolean },
  ) => {
    const currentLanguage = resolveSupportedLanguageCode(i18n);
    const isSameLanguage = languageCode === currentLanguage;

    if (isSameLanguage) {
      if (i18n.language !== languageCode) {
        await i18n.changeLanguage(languageCode);
        publishLanguageChange(i18n.emit.bind(i18n), i18n.language);
      }
      rememberLanguage(languageCode);
      return;
    }

    await i18n.changeLanguage(languageCode);
    publishLanguageChange(i18n.emit.bind(i18n), i18n.language);
    rememberLanguage(i18n.language);

    if (
      options?.reload !== false &&
      typeof window !== "undefined" &&
      shouldReloadPageOnGoogleMapsRoute(window.location.pathname)
    ) {
      window.location.reload();
    }
  };

  return {
    availableLanguages: AVAILABLE_LANGUAGES,
    changeLanguage,
    language: resolveSupportedLanguageCode(i18n),
    translate,
  };
};
